/**
 * AdminEntryAudit.jsx — Forge Entry Audit page
 * Organized card layout: Lexicon Maintenance style.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import LanguageFlag from '../components/LanguageFlag';
import { LexiconPreenrichPanel } from './AdminDashboard';
import './AdminEntryAudit.css';

const API = process.env.REACT_APP_BACKEND_URL;
const LIMIT = 50;
const NA = 'n/a';

const CATEGORIES = {
  1:'Daily Life',2:'Home and Household',3:'Food and Cooking',4:'Clothing and Accessories',
  5:'Body and Anatomy',6:'Medicine and Healthcare',7:'Psychology and Emotions',
  8:'Family and Relationships',9:'People and Professions',10:'Communication',
  11:'Language and Linguistics',12:'Education and Learning',13:'Society and Culture',
  14:'History and Archaeology',15:'Religion and Spirituality',16:'Philosophy and Ethics',
  17:'Mathematics and Statistics',18:'Physics',19:'Chemistry',20:'Biology and Life Sciences',
  21:'Astronomy and Space',22:'Research and Scientific Methods',23:'Nature and Outdoors',
  24:'Arts and Visual Design',25:'Literature and Writing',26:'Film and Entertainment',
  27:'Crafts and Skilled Trades',28:'Materials and Textiles',29:'Measurements and Units',
  30:'Energy and Power Systems',31:'Energy and Electricity',32:'Computing and Technology',
  33:'Engineering',34:'Mechanics and Tools',35:'Construction and Architecture',
  36:'Cities and Infrastructure',37:'Transportation and Vehicles',38:'Manufacturing and Industry',
  39:'Agriculture and Farming',40:'Business and Management',41:'Economics and Finance',
  42:'Law and Legal Systems',43:'Government and Politics',44:'Military and Defense',
  45:'Public Safety and Security',46:'Geography and Cartography',47:'Earth Science and Geology',
  48:'Weather and Climate',49:'Environment and Ecology',50:'Animals and Zoology',
  51:'Plants and Botany',52:'Travel and Tourism',53:'Sports and Fitness',54:'Games and Recreation',
  55:'Music and Performing Arts',56:'Media and Journalism',57:'Shopping and Commerce',
  58:'Real Estate and Property',59:'Law Enforcement and Criminal Justice',60:'General and Miscellaneous',
};

const LANG_OPTIONS = [
  'all','ar','bo','cs','de','en','es','fa','fr','grc','hbo','he','hi','id','it',
  'ja','ko','kea','nl','no','pl','ps','pt','ru','sv','sw','syc','th','tr','uk','vi','zh',
];

const FILTER_OPTIONS = [
  { value: 'any',          label: 'Missing any tracked field' },
  { value: 'pronunciation',label: 'Missing pronunciation (IPA)' },
  { value: 'meaning',      label: 'Missing meaning (definitions)' },
  { value: 'usage',        label: 'Missing usage (collocates/examples)' },
  { value: 'grammar',      label: 'Missing grammar (pos/lemma)' },
  { value: 'categories',   label: 'Missing categories' },
  { value: 'all',          label: 'All entries' },
];

const arrToText = v => Array.isArray(v) ? v.join('\n') : (v || '');
const textToArr = t => t.split('\n').map(s => s.trim()).filter(Boolean);

// Key: value per-line helpers for morphology / inflection objects
const objToText = v => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return '';
  return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join('\n');
};
const textToObj = t => {
  const obj = {};
  t.split('\n').forEach(line => {
    const idx = line.indexOf(':');
    if (idx > 0) obj[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  });
  return obj;
};

function isMissing(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '' || v.trim().toLowerCase() === NA;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

// ── Small dot indicator next to field label ───────────────────────────────
function MissingDot({ missing, naValue }) {
  if (naValue) return <span className="field-na-dot" title="Marked N/A" />;
  if (missing) return <span className="field-missing-dot" title="Missing" />;
  return null;
}

// ── Single field (input or textarea) ─────────────────────────────────────
function AuditField({ label, name, value, onChange, type = 'input', isList = false, missing, readOnly = false }) {
  const isNa = typeof value === 'string' && value.trim().toLowerCase() === NA;
  const displayVal = isList ? arrToText(value) : (value ?? '');
  const hasValue = !isMissing(value);

  const labelEl = (
    <label className="field-label">
      <MissingDot missing={missing && !isNa} naValue={isNa} />
      {label}
      {isNa && <span className="field-na-badge">n/a</span>}
    </label>
  );

  if (type === 'textarea') {
    return (
      <div className="field-group">
        {labelEl}
        <textarea
          value={displayVal}
          onChange={e => !readOnly && onChange(name, isList ? textToArr(e.target.value) : e.target.value)}
          rows={isList ? 4 : 3}
          className={isNa ? 'is-na' : ''}
          placeholder={isNa ? 'N/A' : ''}
          readOnly={readOnly}
        />
      </div>
    );
  }
  return (
    <div className="field-group">
      {labelEl}
      <input
        value={displayVal}
        onChange={e => !readOnly && onChange(name, e.target.value)}
        className={readOnly ? 'read-only' : hasValue && !isNa ? 'has-value' : isNa ? 'is-na' : ''}
        placeholder={isNa ? 'N/A' : ''}
        readOnly={readOnly}
      />
    </div>
  );
}

// ── Editor section card ───────────────────────────────────────────────────
function EditorCard({ title, status, children }) {
  // status: true = missing, false = ok, undefined = no badge
  return (
    <div className="editor-card">
      <div className="editor-card-head">
        <span className="editor-card-title">{title}</span>
        {status != null && (
          <span className={`card-status-badge ${status ? 'badge-missing' : 'badge-ok'}`}>
            {status ? 'Missing' : 'OK'}
          </span>
        )}
      </div>
      <div className="editor-card-body">{children}</div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function AdminEntryAudit() {
  const [language, setLanguage] = useState('all');
  const [filterType, setFilterType] = useState('any');
  const [search, setSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [refreshJob, setRefreshJob] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [markingNa, setMarkingNa] = useState(false);
  const [markNaResult, setMarkNaResult] = useState(null);
  const [auditSummary, setAuditSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const fetchEntries = useCallback(async (s = skip) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/word-lexicon-audit`, {
        params: { language, filter: filterType, search, limit: LIMIT, skip: s },
        withCredentials: true,
      });
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [language, filterType, search, skip]);

  useEffect(() => { fetchEntries(0); setSkip(0); }, [language, filterType]);

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/word-lexicon-audit/summary`, { withCredentials: true });
      setAuditSummary(res.data);
    } catch (err) {
      toast.error('Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Poll bulk-refresh job while running
  useEffect(() => {
    if (!refreshJob || (refreshJob.status !== 'running' && refreshJob.status !== 'starting')) return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/admin/word-lexicon-audit/bulk-refresh/status`, { withCredentials: true });
        setRefreshJob(r.data);
        if (r.data.status === 'complete' || r.data.status === 'cancelled') fetchEntries(skip);
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(id);
  }, [refreshJob, skip]);

  const handleSearch = () => { setSkip(0); fetchEntries(0); };

  const handleSelect = (entry) => {
    setSelected(entry);
    setDraft(JSON.parse(JSON.stringify(entry)));
    setDirty(false);
  };

  const handleChange = (field, value) => {
    setDraft(d => ({ ...d, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!draft || !selected) return;
    setSaving(true);
    try {
      const payload = {};
      const TRACKED = ['ipa','ipa_variants','zhuyin','jyutping','romaji','rr',
        'definitions','synonyms','translation_en','collocates','derivations',
        'examples','related_expressions','pos','lemma','morphology','inflection','categories'];
      for (const k of TRACKED) {
        if (JSON.stringify(draft[k]) !== JSON.stringify(selected[k])) payload[k] = draft[k];
      }
      if (Object.keys(payload).length === 0) { setDirty(false); setSaving(false); return; }
      await axios.patch(
        `${API}/api/admin/word-lexicon-audit/${selected.language_code}/${selected.word}`,
        payload, { withCredentials: true }
      );
      toast.success('Entry saved');
      setEntries(prev => prev.map(e =>
        e.word === selected.word && e.language_code === selected.language_code ? { ...e, ...payload } : e
      ));
      setSelected(d => ({ ...d, ...payload }));
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setDraft(JSON.parse(JSON.stringify(selected))); setDirty(false); };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.word}" (${selected.language_code}) permanently from the lexicon?`)) return;
    try {
      await axios.delete(
        `${API}/api/admin/word-lexicon-audit/${selected.language_code}/${selected.word}`,
        { withCredentials: true }
      );
      toast.success(`Deleted "${selected.word}"`);
      setEntries(prev => prev.filter(e => !(e.word === selected.word && e.language_code === selected.language_code)));
      setSelected(null);
      setDraft(null);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const entryKey = e => `${e.language_code}::${e.word}`;
  const toggleCheck = (e, ev) => {
    ev.stopPropagation();
    setChecked(prev => { const next = new Set(prev); const k = entryKey(e); next.has(k) ? next.delete(k) : next.add(k); return next; });
  };
  const toggleAll = () => {
    if (checked.size === entries.length) setChecked(new Set());
    else setChecked(new Set(entries.map(entryKey)));
  };

  const startBulkRefresh = async () => {
    const toRefresh = entries.filter(e => checked.has(entryKey(e))).map(e => ({ word: e.word, language_code: e.language_code }));
    if (!toRefresh.length) return;
    setRefreshing(true);
    try {
      await axios.post(`${API}/api/admin/word-lexicon-audit/bulk-refresh`, toRefresh, { withCredentials: true });
      const r = await axios.get(`${API}/api/admin/word-lexicon-audit/bulk-refresh/status`, { withCredentials: true });
      setRefreshJob(r.data);
      setChecked(new Set());
      toast.success(`Refresh queued for ${toRefresh.length} entries`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const cancelBulkRefresh = async () => {
    try { await axios.post(`${API}/api/admin/word-lexicon-audit/bulk-refresh/cancel`, null, { withCredentials: true }); }
    catch { /* ignore */ }
  };

  const markInapplicableFields = async () => {
    if (!window.confirm(
      'This will set language-inapplicable transcription fields (zhuyin, jyutping, romaji, rr, ipa/ipa_variants) to "n/a" for all entries where they are currently empty.\n\nExisting non-empty values will NOT be overwritten. Continue?'
    )) return;
    setMarkingNa(true);
    setMarkNaResult(null);
    try {
      const res = await axios.post(`${API}/api/admin/word-lexicon-audit/mark-inapplicable`, null, { withCredentials: true });
      setMarkNaResult(res.data);
      toast.success(res.data.message);
      fetchEntries(skip);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark n/a');
    } finally {
      setMarkingNa(false);
    }
  };

  const setCatSlot = (slotIdx, val) => {
    const cur = [...(draft?.categories || [])];
    while (cur.length <= slotIdx) cur.push(null);
    cur[slotIdx] = val ? Number(val) : null;
    const cleaned = [...new Set(cur.filter(Boolean))].slice(0, 3);
    handleChange('categories', cleaned);
  };

  const groups = draft ? (draft._missing_groups || []) : [];

  return (
    <div className="audit-root">

      {/* ── Pre-Enrichment Panel (moved from Admin Dashboard) ── */}
      <LexiconPreenrichPanel />

      {/* ── Page title ── */}
      <div className="page-header">
        <div className="page-title-area">
          <span className="page-eyebrow">Lexicon Maintenance</span>
          <h1 className="page-title">Entry Audit</h1>
          <p className="page-desc">Find incomplete entries, review every field, then save or lock canonized terms.</p>
        </div>
      </div>

      {/* ── Search card ── */}
      <div className="search-card">
        <div className="search-fields-row">
          <div className="search-col">
            <label className="search-col-label">Language</label>
            <select
              value={language}
              onChange={e => setLanguage(e.target.value)}
              data-testid="audit-language-select"
            >
              {LANG_OPTIONS.map(l => (
                <option key={l} value={l}>{l === 'all' ? 'All languages' : l.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div className="search-col search-col-wide">
            <label className="search-col-label">Search Criteria</label>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              data-testid="audit-filter-select"
            >
              {FILTER_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="search-col search-col-grow">
            <label className="search-col-label">Specific Entry Search</label>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Enter word or partial word…"
              data-testid="audit-search-input"
            />
          </div>
          <button className="btn-search-main" onClick={handleSearch} data-testid="audit-search-btn">
            Search entries
          </button>
          <button
            className={`btn-summary-toggle${showSummary ? ' active' : ''}`}
            onClick={() => { setShowSummary(s => !s); if (!auditSummary && !summaryLoading) fetchSummary(); }}
            data-testid="audit-summary-toggle-btn"
          >
            {summaryLoading ? 'Loading…' : 'Summary'}
          </button>
        </div>
        <div className="search-meta-row">
          <span className="search-entry-count">{total.toLocaleString()} entries found</span>
        </div>
      </div>

      {/* ── Per-language summary table ── */}
      {showSummary && auditSummary && (
        <div className="audit-summary-bar">
          <table className="audit-summary-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Lang</th>
                <th>Total</th>
                <th>Pron</th>
                <th>Meaning</th>
                <th>Usage</th>
                <th>Grammar</th>
                <th>Cats</th>
              </tr>
            </thead>
            <tbody>
              {auditSummary
                .slice()
                .sort((a, b) =>
                  (b.missing_pronunciation + b.missing_meaning + b.missing_grammar + b.missing_categories) -
                  (a.missing_pronunciation + a.missing_meaning + a.missing_grammar + a.missing_categories)
                )
                .map(row => (
                  <tr
                    key={row.language_code}
                    className={language === row.language_code ? 'summary-row-active' : ''}
                    onClick={() => { setLanguage(row.language_code); setSkip(0); fetchEntries(0); }}
                    title={`Filter by ${row.language_code}`}
                  >
                    <td className="summary-lang">{row.language_code}</td>
                    <td>{row.total.toLocaleString()}</td>
                    <td className={row.missing_pronunciation > 0 ? 'cell-bad' : 'cell-ok'}>{row.missing_pronunciation}</td>
                    <td className={row.missing_meaning > 0 ? 'cell-bad' : 'cell-ok'}>{row.missing_meaning}</td>
                    <td className={row.missing_usage > 0 ? 'cell-warn' : 'cell-ok'}>{row.missing_usage}</td>
                    <td className={row.missing_grammar > 0 ? 'cell-bad' : 'cell-ok'}>{row.missing_grammar}</td>
                    <td className={row.missing_categories > 0 ? 'cell-bad' : 'cell-ok'}>{row.missing_categories}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Bulk toolbar ── */}
      <div className="bulk-toolbar">
        <input
          type="checkbox"
          style={{ cursor: 'pointer', accentColor: '#e8621a' }}
          checked={entries.length > 0 && checked.size === entries.length}
          onChange={toggleAll}
          title="Select / deselect all on this page"
          data-testid="audit-select-all"
        />
        <span className="bulk-select-label">{checked.size > 0 ? `${checked.size} selected` : 'Select'}</span>

        {checked.size > 0 && (
          <button
            className="bulk-btn"
            onClick={startBulkRefresh}
            disabled={refreshing || refreshJob?.status === 'running' || refreshJob?.status === 'starting'}
            data-testid="audit-bulk-refresh-btn"
          >
            {refreshing ? 'Starting…' : `AI Refresh (${checked.size})`}
          </button>
        )}

        {refreshJob && (refreshJob.status === 'running' || refreshJob.status === 'complete') && (
          <span className={`bulk-job-status ${refreshJob.status === 'complete' ? 'job-done' : 'job-running'}`}>
            {refreshJob.status === 'running'
              ? `${refreshJob.done}/${refreshJob.total} · ${refreshJob.refreshed}r ${refreshJob.enriched}e ${refreshJob.failed}f`
              : `Done — ${refreshJob.refreshed}r ${refreshJob.enriched}e ${refreshJob.failed}f`}
          </span>
        )}
        {refreshJob?.status === 'running' && (
          <button className="bulk-btn bulk-btn-cancel" onClick={cancelBulkRefresh}>Cancel</button>
        )}

        <button
          className="bulk-btn bulk-btn-na"
          style={{ marginLeft: 'auto' }}
          onClick={markInapplicableFields}
          disabled={markingNa}
          title="Set language-inapplicable transcription fields to n/a for all currently empty entries"
          data-testid="audit-mark-inapplicable-btn"
        >
          {markingNa ? 'Working…' : 'Mark Inapplicable N/A'}
        </button>
        {markNaResult && (
          <span className="bulk-na-result">{markNaResult.total_fields_updated.toLocaleString()} values set to n/a</span>
        )}
      </div>

      {/* ── Workspace ── */}
      <div className="audit-workspace">

        {/* Left — entry list */}
        <div className="entries-panel">
          <div className="entries-panel-head">
            <span>Matching entries</span>
            <span className="entries-count-badge">{total.toLocaleString()} entries</span>
          </div>

          <div className="entries-list">
            {loading && <div className="entries-loading">Loading…</div>}
            {!loading && entries.length === 0 && (
              <div className="entries-empty">No entries found</div>
            )}
            {entries.map(e => {
              const mg = e._missing_groups || [];
              const isActive = selected?.word === e.word && selected?.language_code === e.language_code;
              const isChecked = checked.has(entryKey(e));
              return (
                <div
                  key={`${e.language_code}-${e.word}`}
                  className={`entry-item${isActive ? ' active' : ''}`}
                  onClick={() => handleSelect(e)}
                  data-testid={`audit-entry-${e.language_code}-${e.word}`}
                >
                  <div className="entry-row-top">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={ev => toggleCheck(e, ev)}
                      onClick={ev => ev.stopPropagation()}
                      style={{ cursor: 'pointer', accentColor: '#e8621a', marginRight: 6 }}
                    />
                    <span className="entry-word">{e.word}</span>
                    {e.ai_enriched && <span className="entry-ai-badge">AI</span>}
                  </div>
                  <div className="entry-subtitle">
                    <LanguageFlag code={e.language_code} size="sm" />
                    {e.language_code}{e.pos ? ` · ${e.pos}` : ''}
                  </div>
                  <div className="entry-missing-line">
                    {mg.length === 0
                      ? <span className="em-ok">complete</span>
                      : <span className="em-list">{mg.join(' · ')}</span>
                    }
                  </div>
                </div>
              );
            })}
          </div>

          <div className="entries-pagination">
            <button
              disabled={skip === 0}
              onClick={() => { const s = Math.max(0, skip - LIMIT); setSkip(s); fetchEntries(s); }}
            >← Prev</button>
            <span>{skip + 1}–{Math.min(skip + LIMIT, total)} of {total.toLocaleString()}</span>
            <button
              disabled={skip + LIMIT >= total}
              onClick={() => { const s = skip + LIMIT; setSkip(s); fetchEntries(s); }}
            >Next →</button>
          </div>
        </div>

        {/* Right — editor */}
        <div className="editor-panel">
          {!selected ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">⬛</div>
              <div>Select an entry from the list to edit</div>
              <div className="editor-empty-hint">Empty search shows entries with missing data</div>
            </div>
          ) : (
            <>
              {/* Editor header bar */}
              <div className="editor-header-bar">
                <div>
                  <div className="editor-word-title">{selected.word}</div>
                  <div className="editor-word-meta">
                    <LanguageFlag code={selected.language_code} size="sm" />
                    {' '}{selected.language_code}
                    {selected.pos ? ` · ${selected.pos}` : ''}
                    {selected.lemma ? ` · lemma: ${selected.lemma}` : ''}
                    {' · '}<span className={groups.length > 0 ? 'meta-missing' : 'meta-ok'}>
                      {groups.length} field group{groups.length !== 1 ? 's' : ''} incomplete
                    </span>
                  </div>
                </div>
                <div className="editor-action-btns">
                  <button className="btn btn-delete" onClick={handleDelete} title="Delete this entry permanently" data-testid="audit-delete-btn">
                    Delete
                  </button>
                  <button className="btn btn-na" onClick={() => {
                    const updates = {};
                    const simple = ['ipa','translation_en','pos','lemma'];
                    const lists = ['definitions','synonyms','collocates','derivations','examples','related_expressions'];
                    [...simple, ...lists].forEach(f => { if (isMissing(draft[f])) updates[f] = NA; });
                    setDraft(d => ({ ...d, ...updates }));
                    setDirty(true);
                  }} title="Mark all empty fields as N/A">Mark N/A</button>
                  <button className="btn btn-reset" onClick={handleReset} disabled={!dirty}>Reset</button>
                  <button
                    className="btn btn-save"
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    data-testid="audit-save-btn"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              {/* Identity */}
              <EditorCard title="Identity">
                <div className="fields-grid-2">
                  <AuditField label="Word / Surface Form" name="word" value={selected.word} onChange={() => {}} readOnly />
                  <div className="field-group">
                    <label className="field-label">Language Code</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <LanguageFlag code={selected.language_code} size="md" />
                      <input value={selected.language_code} readOnly className="read-only" style={{ flex: 1 }} />
                    </div>
                  </div>
                </div>
              </EditorCard>

              {/* Pronunciation */}
              <EditorCard title="Pronunciation" status={groups.includes('pronunciation')}>
                <div className="fields-grid-2">
                  <AuditField label="IPA" name="ipa" value={draft.ipa} onChange={handleChange} missing={isMissing(draft.ipa)} />
                  <AuditField label="IPA Variants (AR / ES / PT / EN)" name="ipa_variants" value={draft.ipa_variants} onChange={handleChange} type="textarea" isList={true} missing={isMissing(draft.ipa_variants)} />
                  <AuditField label="Zhuyin / Jyutping (ZH)" name="zhuyin" value={draft.zhuyin} onChange={handleChange} missing={isMissing(draft.zhuyin)} />
                  <AuditField label="Romaji (JA)" name="romaji" value={draft.romaji} onChange={handleChange} missing={isMissing(draft.romaji)} />
                  <AuditField label="Jyutping — Cantonese (ZH)" name="jyutping" value={draft.jyutping} onChange={handleChange} missing={isMissing(draft.jyutping)} />
                  <AuditField label="RR — Revised Romanization (KO)" name="rr" value={draft.rr} onChange={handleChange} missing={isMissing(draft.rr)} />
                </div>
              </EditorCard>

              {/* Meanings */}
              <EditorCard title="Meanings" status={groups.includes('meaning')}>
                <div className="fields-grid-2">
                  <AuditField label="Part of Speech" name="pos" value={draft.pos} onChange={handleChange} missing={isMissing(draft.pos)} />
                  <AuditField label="English Translation" name="translation_en" value={draft.translation_en} onChange={handleChange} missing={isMissing(draft.translation_en)} />
                </div>
                <div className="fields-grid-1">
                  <AuditField
                    label="Definitions (one per line)"
                    name="definitions"
                    value={draft.definitions}
                    onChange={handleChange}
                    type="textarea"
                    isList={true}
                    missing={isMissing(draft.definitions)}
                  />
                  <AuditField label="Synonyms (one per line)" name="synonyms" value={draft.synonyms} onChange={handleChange} type="textarea" isList={true} missing={isMissing(draft.synonyms)} />
                </div>
              </EditorCard>

              {/* Grammar */}
              <EditorCard title="Grammar" status={groups.includes('grammar')}>
                <div className="fields-grid-2">
                  <AuditField label="Lemma" name="lemma" value={draft.lemma} onChange={handleChange} missing={isMissing(draft.lemma)} />
                  <AuditField
                    label="Morphology (key: value, one per line)"
                    name="morphology"
                    value={objToText(draft.morphology)}
                    onChange={(n, v) => handleChange(n, textToObj(v))}
                    type="textarea"
                    missing={isMissing(draft.morphology)}
                  />
                  <AuditField
                    label="Inflection (key: value, one per line)"
                    name="inflection"
                    value={objToText(draft.inflection)}
                    onChange={(n, v) => handleChange(n, textToObj(v))}
                    type="textarea"
                    missing={isMissing(draft.inflection)}
                  />
                </div>
              </EditorCard>

              {/* Usage */}
              <EditorCard title="Usage" status={groups.includes('usage')}>
                <div className="fields-grid-2">
                  <AuditField label="Collocates (one per line)" name="collocates" value={draft.collocates} onChange={handleChange} type="textarea" isList={true} missing={isMissing(draft.collocates)} />
                  <AuditField label="Examples (one per line)" name="examples" value={draft.examples} onChange={handleChange} type="textarea" isList={true} missing={isMissing(draft.examples)} />
                  <AuditField label="Derivations (one per line)" name="derivations" value={draft.derivations} onChange={handleChange} type="textarea" isList={true} missing={isMissing(draft.derivations)} />
                  <AuditField label="Related Expressions (one per line)" name="related_expressions" value={draft.related_expressions} onChange={handleChange} type="textarea" isList={true} missing={isMissing(draft.related_expressions)} />
                </div>
              </EditorCard>

              {/* Categories */}
              <EditorCard title="Categories" status={groups.includes('categories')}>
                <div className="fields-grid-1">
                  {[0, 1, 2].map(slot => (
                    <div className="field-group" key={slot}>
                      <label className="field-label">
                        Category {slot + 1}
                        {slot > 0 && <span style={{ color: '#484f58', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> — optional</span>}
                      </label>
                      <select
                        value={(draft.categories || [])[slot] || ''}
                        onChange={e => setCatSlot(slot, e.target.value)}
                      >
                        <option value="">— None —</option>
                        {(() => {
                          const entries = Object.entries(CATEGORIES);
                          // "General and Miscellaneous" (ID 60) always on top, rest alphabetical
                          const top = entries.filter(([, name]) => name === 'General and Miscellaneous');
                          const rest = entries
                            .filter(([, name]) => name !== 'General and Miscellaneous')
                            .sort(([, a], [, b]) => a.localeCompare(b));
                          return [...top, ...rest].map(([id, name]) => (
                            <option key={id} value={id}>{name}</option>
                          ));
                        })()}
                      </select>
                    </div>
                  ))}
                </div>
              </EditorCard>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
